import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { promoCodesAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { Plus, Trash2, Tag, Percent, DollarSign, Edit, Copy, CheckCircle, XCircle, Clock, Users, BarChart3, Gift } from 'lucide-react';

const PromoCodes = () => {
  const { toast } = useToast();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showDelete, setShowDelete] = useState(null);
  const [activeTab, setActiveTab] = useState('manage');
  const [usageReport, setUsageReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [form, setForm] = useState({
    code: '', discount_type: 'percentage', discount_value: '',
    expiry_date: '', max_uses: '', one_per_customer: true,
  });
  const [formLoading, setFormLoading] = useState(false);

  const load = async () => {
    try {
      const data = await promoCodesAPI.getAll();
      setCodes(data);
    } catch {
      toast({ title: 'Error', description: 'Failed to load promo codes', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const loadReport = async () => {
    setReportLoading(true);
    try {
      const data = await promoCodesAPI.getUsageReport();
      setUsageReport(data);
    } catch {
      toast({ title: 'Error', description: 'Failed to load usage report', variant: 'destructive' });
    } finally { setReportLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (activeTab === 'report' && !usageReport) loadReport(); }, [activeTab]);

  const resetForm = () => {
    setForm({ code: '', discount_type: 'percentage', discount_value: '', expiry_date: '', max_uses: '', one_per_customer: true });
    setEditId(null);
  };

  const openCreate = () => { resetForm(); setShowForm(true); };

  const openEdit = (c) => {
    setForm({
      code: c.code, discount_type: c.discount_type,
      discount_value: String(c.discount_value),
      expiry_date: c.expiry_date ? c.expiry_date.slice(0, 16) : '',
      max_uses: c.max_uses ? String(c.max_uses) : '',
      one_per_customer: c.one_per_customer,
    });
    setEditId(c.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.code.trim()) {
      toast({ title: 'Required', description: 'Promo code is required', variant: 'destructive' });
      return;
    }
    if (!form.discount_value || parseFloat(form.discount_value) <= 0) {
      toast({ title: 'Required', description: 'Discount value must be positive', variant: 'destructive' });
      return;
    }
    setFormLoading(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        discount_type: form.discount_type,
        discount_value: parseFloat(form.discount_value),
        expiry_date: form.expiry_date ? new Date(form.expiry_date).toISOString() : null,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        one_per_customer: form.one_per_customer,
      };
      if (editId) {
        await promoCodesAPI.update(editId, payload);
        toast({ title: 'Updated', description: `Promo code ${payload.code} updated` });
      } else {
        await promoCodesAPI.create(payload);
        toast({ title: 'Created', description: `Promo code ${payload.code} created` });
      }
      setShowForm(false);
      resetForm();
      load();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setFormLoading(false); }
  };

  const handleDelete = async () => {
    try {
      await promoCodesAPI.delete(showDelete.id);
      toast({ title: 'Deleted', description: `Promo code ${showDelete.code} deleted` });
      setShowDelete(null);
      load();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const handleToggleStatus = async (c) => {
    try {
      await promoCodesAPI.update(c.id, { status: c.status === 'active' ? 'inactive' : 'active' });
      toast({ title: 'Updated', description: `${c.code} is now ${c.status === 'active' ? 'inactive' : 'active'}` });
      load();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      toast({ title: 'Copied', description: `${code} copied to clipboard` });
    }).catch(() => {});
  };

  const formatDate = (iso) => {
    if (!iso) return 'No expiry';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const isExpired = (iso) => {
    if (!iso) return false;
    return new Date(iso) < new Date();
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-600" /></div></Layout>;

  const activeCodes = codes.filter(c => c.status === 'active');
  const inactiveCodes = codes.filter(c => c.status !== 'active');

  return (
    <Layout>
      <div className="space-y-5" data-testid="promo-codes-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Tag className="h-6 w-6 text-teal-600" /> Promo Codes
            </h1>
            <p className="text-sm text-gray-500 mt-1">{activeCodes.length} active, {inactiveCodes.length} inactive</p>
          </div>
          <div className="flex gap-2">
            <Button variant={activeTab === 'manage' ? 'default' : 'outline'} onClick={() => setActiveTab('manage')} data-testid="tab-manage">
              <Tag className="h-4 w-4 mr-1" /> Manage
            </Button>
            <Button variant={activeTab === 'report' ? 'default' : 'outline'} onClick={() => setActiveTab('report')} data-testid="tab-usage-report">
              <BarChart3 className="h-4 w-4 mr-1" /> Usage Report
            </Button>
          </div>
        </div>

        {activeTab === 'report' ? (
          <PromoUsageReport data={usageReport} loading={reportLoading} onRefresh={loadReport} />
        ) : (
        <>
        <div className="flex justify-end">
          <Button onClick={openCreate} className="bg-gradient-to-r from-sky-500 to-teal-500" data-testid="create-promo-btn">
            <Plus className="h-4 w-4 mr-1" /> Create Promo Code
          </Button>
        </div>

        {codes.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border">
            <Tag className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Promo Codes Yet</h3>
            <p className="text-sm text-slate-500 mb-4">Create your first promo code to offer discounts to customers.</p>
            <Button onClick={openCreate} className="bg-gradient-to-r from-sky-500 to-teal-500" data-testid="create-first-promo-btn">
              <Plus className="h-4 w-4 mr-1" /> Create Promo Code
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-100 border-b">
                    <th className="text-left px-5 py-3 font-semibold text-slate-600">Code</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Discount</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Expiry</th>
                    <th className="text-center px-4 py-3 font-semibold text-slate-600">Uses</th>
                    <th className="text-center px-4 py-3 font-semibold text-slate-600">1/Customer</th>
                    <th className="text-center px-4 py-3 font-semibold text-slate-600">Status</th>
                    <th className="text-right px-5 py-3 font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {codes.map(c => {
                    const expired = isExpired(c.expiry_date);
                    const maxReached = c.max_uses && c.used_count >= c.max_uses;
                    return (
                      <tr key={c.id} className={`hover:bg-slate-50 ${c.status !== 'active' ? 'opacity-60' : ''}`} data-testid={`promo-row-${c.code}`}>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-slate-800 bg-slate-100 px-2 py-0.5 rounded text-xs">{c.code}</span>
                            <button onClick={() => copyCode(c.code)} className="text-slate-400 hover:text-slate-600" title="Copy code">
                              <Copy className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="flex items-center gap-1 font-semibold text-teal-700">
                            {c.discount_type === 'percentage' ? <><Percent className="h-3.5 w-3.5" /> {c.discount_value}%</> : <><DollarSign className="h-3.5 w-3.5" /> ${c.discount_value}</>}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs flex items-center gap-1 ${expired ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>
                            <Clock className="h-3 w-3" /> {expired ? 'Expired' : formatDate(c.expiry_date)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-xs font-semibold ${maxReached ? 'text-red-600' : 'text-slate-600'}`}>
                            {c.used_count || 0}{c.max_uses ? ` / ${c.max_uses}` : ' / unlimited'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {c.one_per_customer ? (
                            <span className="text-xs text-sky-600 flex items-center justify-center gap-0.5"><Users className="h-3 w-3" /> Yes</span>
                          ) : (
                            <span className="text-xs text-slate-400">No</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button onClick={() => handleToggleStatus(c)} data-testid={`toggle-status-${c.code}`}>
                            {c.status === 'active' ? (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                <CheckCircle className="h-3 w-3" /> Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold bg-slate-200 text-slate-500 px-2 py-0.5 rounded-full">
                                <XCircle className="h-3 w-3" /> Inactive
                              </span>
                            )}
                          </button>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => openEdit(c)} className="p-1.5 rounded hover:bg-sky-50 text-sky-500 hover:text-sky-600"
                              title="Edit" data-testid={`edit-promo-${c.code}`}>
                              <Edit className="h-4 w-4" />
                            </button>
                            <button onClick={() => setShowDelete(c)} className="p-1.5 rounded hover:bg-red-50 text-red-400 hover:text-red-500"
                              title="Delete" data-testid={`delete-promo-${c.code}`}>
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={() => { setShowForm(false); resetForm(); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5 text-teal-600" /> {editId ? 'Edit Promo Code' : 'Create Promo Code'}
            </DialogTitle>
            <DialogDescription>
              {editId ? 'Update the promo code settings.' : 'Set up a new discount code for customers to use at checkout.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Promo Code *</Label>
              <Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="e.g., CLEANAIR10" className="font-mono uppercase" data-testid="promo-code-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Discount Type *</Label>
                <Select value={form.discount_type} onValueChange={v => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger data-testid="promo-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="flat">Flat Amount ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Discount Value *</Label>
                <div className="relative">
                  <Input type="number" step="0.01" min="0" value={form.discount_value}
                    onChange={e => setForm({ ...form, discount_value: e.target.value })}
                    placeholder={form.discount_type === 'percentage' ? 'e.g., 10' : 'e.g., 15.00'}
                    className="pl-8" data-testid="promo-value-input" />
                  <span className="absolute left-3 top-2.5 text-slate-400 text-sm">
                    {form.discount_type === 'percentage' ? '%' : '$'}
                  </span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Expiry Date</Label>
                <Input type="datetime-local" value={form.expiry_date}
                  onChange={e => setForm({ ...form, expiry_date: e.target.value })}
                  data-testid="promo-expiry-input" />
              </div>
              <div className="space-y-2">
                <Label>Max Total Uses</Label>
                <Input type="number" min="1" value={form.max_uses}
                  onChange={e => setForm({ ...form, max_uses: e.target.value })}
                  placeholder="Unlimited" data-testid="promo-max-uses-input" />
              </div>
            </div>
            <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
              <input type="checkbox" id="one_per_customer" checked={form.one_per_customer}
                onChange={e => setForm({ ...form, one_per_customer: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                data-testid="promo-one-per-customer" />
              <label htmlFor="one_per_customer" className="text-sm text-slate-700">
                Limit to one use per customer
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowForm(false); resetForm(); }}>Cancel</Button>
            <Button onClick={handleSave} disabled={formLoading}
              className="bg-gradient-to-r from-sky-500 to-teal-500" data-testid="save-promo-btn">
              {formLoading ? 'Saving...' : editId ? 'Update Code' : 'Create Code'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!showDelete} onOpenChange={() => setShowDelete(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete Promo Code</DialogTitle></DialogHeader>
          <DialogDescription>
            Are you sure you want to delete <strong>{showDelete?.code}</strong>? This cannot be undone.
          </DialogDescription>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDelete(null)}>Cancel</Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-promo">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default PromoCodes;

// ── Promo Usage Report Component ─────────────────────────────────────
const PromoUsageReport = ({ data, loading, onRefresh }) => {
  const [expandedCode, setExpandedCode] = useState(null);

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-600" />
    </div>
  );

  if (!data) return null;

  const { promo_codes = [], freebies = [] } = data;
  const totalDiscounts = promo_codes.reduce((s, c) => s + (c.total_discount_given || 0), 0);
  const totalRevenue = promo_codes.reduce((s, c) => s + (c.total_revenue_generated || 0), 0);

  return (
    <div className="space-y-6">
      {/* KPI Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-5 pb-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Total Codes</p>
            <p className="text-2xl font-bold text-slate-800 mt-1" data-testid="report-total-codes">{promo_codes.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Total Uses</p>
            <p className="text-2xl font-bold text-slate-800 mt-1" data-testid="report-total-uses">
              {promo_codes.reduce((s, c) => s + (c.used_count || 0), 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Total Discounts Given</p>
            <p className="text-2xl font-bold text-red-600 mt-1" data-testid="report-total-discounts">
              ${totalDiscounts.toFixed(2)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Revenue from Promo Orders</p>
            <p className="text-2xl font-bold text-green-600 mt-1" data-testid="report-total-revenue">
              ${totalRevenue.toFixed(2)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Per-code breakdown */}
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Tag className="h-5 w-5 text-teal-600" /> Code Performance
          </CardTitle>
          <Button variant="outline" size="sm" onClick={onRefresh} data-testid="refresh-report-btn">Refresh</Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="promo-usage-table">
              <thead>
                <tr className="bg-slate-100 text-slate-600 text-left border-b">
                  <th className="px-4 py-2 font-medium">Code</th>
                  <th className="px-4 py-2 font-medium">Discount</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium text-right">Uses</th>
                  <th className="px-4 py-2 font-medium text-right">Discount Given</th>
                  <th className="px-4 py-2 font-medium text-right">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {promo_codes.map(c => (
                  <React.Fragment key={c.id}>
                    <tr
                      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                      onClick={() => setExpandedCode(expandedCode === c.id ? null : c.id)}
                      data-testid={`report-row-${c.code}`}
                    >
                      <td className="px-4 py-2">
                        <span className="font-mono font-bold text-slate-800 bg-slate-100 px-2 py-0.5 rounded text-xs">{c.code}</span>
                      </td>
                      <td className="px-4 py-2 text-teal-700 font-medium">
                        {c.discount_type === 'percentage' ? `${c.discount_value}%` : `$${c.discount_value}`}
                      </td>
                      <td className="px-4 py-2">
                        <Badge variant={c.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                          {c.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-2 text-right font-semibold">{c.used_count || 0}</td>
                      <td className="px-4 py-2 text-right text-red-600">${(c.total_discount_given || 0).toFixed(2)}</td>
                      <td className="px-4 py-2 text-right text-green-600">${(c.total_revenue_generated || 0).toFixed(2)}</td>
                    </tr>
                    {expandedCode === c.id && c.orders?.length > 0 && (
                      <tr>
                        <td colSpan={6} className="bg-slate-50 px-6 py-3">
                          <p className="text-xs font-medium text-slate-500 mb-2">Orders using {c.code}:</p>
                          <div className="space-y-1">
                            {c.orders.map((o, i) => (
                              <div key={i} className="flex items-center justify-between text-xs text-slate-600 py-1 border-b border-slate-200 last:border-0">
                                <span className="font-medium">{o.order_id}</span>
                                <span>{o.customer_name}</span>
                                <span className="text-red-500">-${(o.discount_applied || 0).toFixed(2)}</span>
                                <span className="text-green-600">${(o.order_total || 0).toFixed(2)}</span>
                                <Badge variant="outline" className="text-xs">{o.payment_status}</Badge>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Freebies section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Gift className="h-5 w-5 text-amber-500" /> Freebies Given During Delivery
          </CardTitle>
        </CardHeader>
        <CardContent>
          {freebies.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">No freebies recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="freebies-report-table">
                <thead>
                  <tr className="bg-slate-100 text-slate-600 text-left border-b">
                    <th className="px-4 py-2 font-medium">Order</th>
                    <th className="px-4 py-2 font-medium">Customer</th>
                    <th className="px-4 py-2 font-medium">Item</th>
                    <th className="px-4 py-2 font-medium text-right">Qty</th>
                    <th className="px-4 py-2 font-medium">Given By</th>
                    <th className="px-4 py-2 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {freebies.map((fb, idx) => (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-2 font-medium text-slate-800">{fb.order_id}</td>
                      <td className="px-4 py-2 text-slate-600">{fb.customer_name}</td>
                      <td className="px-4 py-2 text-slate-700">{fb.description}</td>
                      <td className="px-4 py-2 text-right font-semibold">{fb.quantity}</td>
                      <td className="px-4 py-2 text-slate-500 text-xs">{fb.given_by}</td>
                      <td className="px-4 py-2 text-slate-400 text-xs">
                        {fb.given_at ? new Date(fb.given_at).toLocaleDateString() : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
